<script>
import {mapActions} from "vuex";

export default {
  name: "AfterDisposedItemModal",
  props: {
    /**
     * モーダルの表示制御
     */
    enable: {
      type: Boolean,
      default: false
    },
    /**
     * 処理後物の種類
     * after_industrial: 廃棄物
     * after_recycling: 再資源
     */
    after_type: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      /**
       * ツリーデータ
       * @property after_disposal_items { Array }
       */
      after_disposal_items: [],
      /**
       * 選択値
       */
      recycle_disposed_item_ids: null,
      /**
       * 画面表示時の選択値
       */
      original_recycle_disposed_item_ids: [],
      loading: false,
      /** 全選択・全解除 */
      check_control: false
    }
  },
  watch: {
    enable (val) {
      this.loading = true;
      this.after_disposal_items = [];
      this.recycle_disposed_item_ids = null;
      this.original_recycle_disposed_item_ids = [];
      this.check_control = false;
      if (val) {
        this.loadAfterDisposedItems();
      }
    }
  },
  methods: {
    ...mapActions("DriverMasterAfterDisposalItem", {
      fetchAfterDisposedItems: "index",
      createAfterDisposedItem: "create"
    }),
    ...mapActions("snackbar", {
      showMultiError: "showMultiError",
      showInfo: "showInfo"
    }),

    /**
     * 全選択/全解除チェックボックス クリックイベント
     */
    checkControlClickEvent () {
      const extractIds = (items) => {
        let ids = []
        for (const item of items) {
          if (item.level !== 1) {
            ids.push(item.id)
          }
          if (item.children) {
            ids.push(extractIds(item.children))
          }
        }
        return ids.flat()
      }
      if (this.check_control) {
        this.recycle_disposed_item_ids = extractIds(this.after_disposal_items)
      } else {
        this.recycle_disposed_item_ids = []
      }
    },
    /**
     * 閉じるボタン クリックイベント
     */
    closeButtonClickEvent () {
      this.$emit('close')
    },

    /**
     * 登録ボタン クリックイベント
     */
    registerButtonClickEvent () {
      // 選択中のID
      const selectedSet = new Set(this.recycle_disposed_item_ids)
      // 表示されているリストから選択中のIDを除き、チェックが外れたIDを抽出
      const unselectedIds = this.original_recycle_disposed_item_ids.filter(id => !selectedSet.has(id))
      const params = {
        after_type: this.after_type,
        register_ids: this.recycle_disposed_item_ids,
        removed_ids: unselectedIds
      }
      this.createAfterDisposedItem({ params: params}).then((data) => {
        if (data.result.code === '422001') {
          this.showMultiError({ message: data.result.message })
        } else {
          this.showInfo({ message: "登録しました" })
          this.$emit('register')
          this.closeButtonClickEvent();
        }
      })
    },

    /**
     * ツリーデータ・選択値の取得
     */
    loadAfterDisposedItems () {
      this.fetchAfterDisposedItems({ after_type: this.after_type }).then((content) => {
        this.after_disposal_items = content.after_disposal_items
        this.recycle_disposed_item_ids = content.recycle_disposed_item_ids
        Object.assign(this.original_recycle_disposed_item_ids, content.recycle_disposed_item_ids)
        this.loading = false
      })
    },

    /**
     * 選択値の保持・削除
     * @param id
     */
    toggleItem(id) {
      const index = this.recycle_disposed_item_ids.indexOf(id)
      if (index === -1) {
        this.recycle_disposed_item_ids.push(id)
      } else {
        this.recycle_disposed_item_ids.splice(index, 1)
      }
    }
  },
}
</script>

<template>
  <v-dialog
    :value="enable"
    persistent
    fullscreen
    scrollable
    transition="dialog-bottom-transition"
    @keydown.esc="closeButtonClickEvent"
    @close="closeButtonClickEvent"
  >
    <v-card flat>
      <v-toolbar
        color="primary"
        dark
        flat
      >
        <v-toolbar-title>処理後物（{{ after_type === 'after_industrial' ? '廃棄物' : '再資源' }}）</v-toolbar-title>
        <v-spacer />
        <v-btn
          :ripple="false"
          dark
          icon
          @click="closeButtonClickEvent"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-divider />
      <v-card-subtitle class="pb-3">
        <v-checkbox
          v-model="check_control"
          :label="check_control ? '全解除' : '全選択'"
          dense
          hide-details="auto"
          @click="checkControlClickEvent"
        />
      </v-card-subtitle>
      <v-card-text
        :class="{ 'fi': loading }"
      >
        <v-treeview
          v-if="recycle_disposed_item_ids"
          ref="tree"
          v-model="recycle_disposed_item_ids"
          :items="after_disposal_items"
          item-key="id"
          open-all
          open-on-click
          selection-type="independent"
          selected-color="primary"
        >
          <template #prepend="{ item }">
            <!-- 親はチェックボックスなし -->
            <div v-if="item.level === 1" style="width: 24px;"></div>

            <!-- 子だけチェックボックス -->
            <v-simple-checkbox
              v-else
              color="primary"
              :value="recycle_disposed_item_ids.includes(item.id)"
              @click.stop="toggleItem(item.id)"
            />
          </template>
          <template v-slot:label="{ item }">
            <span v-if="item.level === 1">
              {{ item.name }}
            </span>
            <span v-else>
            {{ item.code }}: {{ item.name }}
            </span>
          </template>
        </v-treeview>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn @click="closeButtonClickEvent">閉じる</v-btn>
        <!-- チャットボットがあるのでずらす -->
        <v-btn
          color="primary"
          class="mr-15"
          @click="registerButtonClickEvent"
        >
          登録
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss">
.fi {
  height: 87vh;
}
</style>
